import axios from 'axios'
import UserService from './user.service'
import { getProp } from '../libs/utils'
import Alert from '../libs/alert'

const {
  VUE_APP_MONGODB_DATABASE: MONGODB_DATABASE,
  VUE_APP_AUTHORIZER_URL: AUTHORIZER_URL,
  VUE_APP_AUTHORIZER_API_KEY: AUTHORIZER_API_KEY
} = process.env

const _prepareQuery = (query) => {
  return query
}

let previousErrorMessage = null
let previousErrorTime = Date.now()
const request = async (path, query, alert = true, options = {}) => {

  try {
    const isCheckAuthorizeRequest = path === '/check-authorized'
    const serviceUrl = isCheckAuthorizeRequest ? AUTHORIZER_URL : MONGODB_DATABASE
    const accessToken = await UserService.getAccessToken()
    const apiToken = isCheckAuthorizeRequest ? AUTHORIZER_API_KEY : await UserService.getApiToken()
    return await axios.post(`${serviceUrl}${path}`, query, {
      ...options,
      headers: {
        'X-Api-Key': apiToken,
        'Authorization': `Bearer ${accessToken}`,
        ...(options.headers || {})
      },
    })
  } catch (error) {
    console.dir(error)
    if (alert) {
      const message = getProp(error, ['response', 'data', 'error']) || getProp(error, ['message'])
      if (message !== previousErrorMessage || Date.now() - previousErrorTime > 1000) {
        Alert.error({
          title: 'Ooops!',
          message
        })
      }
      previousErrorMessage = message
      previousErrorTime = Date.now()
    }
    throw error
  }
}

const service = {
  request,
  async checkAccessToken () {
    try {
      await request('/check-authorized', undefined, false)
      return true
    } catch (e) {
      return false
    }
  },
  /**
   * Database
  */
  async find (query, database = undefined) {
    query = _prepareQuery({ ...query, count: false })
    const { collection } = query
    return await request(`/database/find/${collection}?${database ? 'database=' + database : ''}`, query).then(r => r.data.result)
  },
  async count (query, database = undefined) {
    query = _prepareQuery({ ...query, count: true })
    const { collection } = query
    return await request(`/database/find/${collection}?${database ? 'database=' + database : ''}`, query).then(r => r.data.result)
  },
  async insert (query, database = undefined) {
    query = _prepareQuery(query)
    const { collection } = query
    return await request(`/database/insert/${collection}?${database ? 'database=' + database : ''}`, query).then(r => r.data.result)
  },
  async update (query, database = undefined) {
    query = _prepareQuery(query)
    const { collection } = query
    return await request(`/database/update/${collection}?${database ? 'database=' + database : ''}`, query).then(r => r.data.result)
  },
  async delete (query, database = undefined) {
    query = _prepareQuery(query)
    const { collection } = query
    return await request(`/database/delete/${collection}?${database ? 'database=' + database : ''}`, query).then(r => r.data.result)
  },
  async aggregate (query, database = undefined) {
    query = _prepareQuery(query)
    const { collection } = query
    return await request(`/database/aggregate/${collection}?${database ? 'database=' + database : ''}`, query).then(r => r.data.result)
  },
  /**
   * Query Shortcut
  */
  query: {
    async getBotConfigsByAccountId (accountId, projection = null) {
      return (await service.find({
        'collection': 'bot-configs',
        'filter': { account_id: accountId },
        'sort': { created_at: -1 },
        projection,
        'limit': 1
      }))[0]
    }
  },
  /**
   * APIs
  */
  api: {
    async historicalTradeExecutions (payload) {
      return await request('/api/historical-trade-executions/execute', payload).then(r => r.data.result)
    },
    async historicalTradeDailyReport (payload) {
      return await request('/api/historical-trade-executions/daily-report', payload).then(r => r.data.result)
    }
  },
  /**
   * Encryption
  */
  encryption: {
    async listKeys () {
      return await request('/encryption/list-keys').then(r => r.data.result)
    },
    async encrypt (query) {
      return await request('/encryption/encrypt', query).then(r => r.data.result)
    }
  },
  /**
   * Balances
  */
  balances: {
    async live () {
      return await request('/balances/live').then(r => r.data.result)
    },
    async hourly (accountId, type, currency = 'XRP', date = new Date()) {
      return await request('/balances/hourly', { account_id: accountId, type, currency, date }).then(r => r.data.result)
    },
    async ripple (accountIds) {
      return await request('/balances/rippleBalance', { 'account_ids': accountIds }).then(r => r.data.result)
    }
  },
  /**
   * Spreadsheet
  */
  async spreadsheet (query = { purpose: 'reporting', days: undefined, client_id: undefined }) {
    return await request('/spreadsheet', query).then(r => r.data.result)
  },
  /**
   * Monitoring
  */
  monitoring: {
    async rate () {
      return await request('/monitoring/rate').then(r => r.data.result)
    },
    async buffers () {
      return await request('/monitoring/buffers').then(r => r.data.result)
    },
    async spreadsheets () {
      return await request('/monitoring/spreadsheets').then(r => r.data.result)
    },
    async bots () {
      return await request('/monitoring/bots').then(r => r.data.result)
    },
    async botGraph (params = {}) {
      return await request('/monitoring/bot-graph', params).then(r => r.data.result)
    }
  },
  sales: {
    async updateVirtualBalances ({ currency }) {
      return await request('/sales/update-virtual-balances', { token: currency}).then(r => r.data.result)
    },
    async monitoring ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/monitoring', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async wakeUp ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/wake-up', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async updateWakeUp ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/update-wake-up', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async sendClientsToDaily ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/send-clients-to-daily', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async sendDailyToExchanges ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/send-daily-to-exchanges', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async updateSpreadsheet () {
      return await request('/sales/update-spreadsheet').then(r => r.data.result)
    },
    async sendExchangesToBitstamp ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/send-exchanges-to-bitstamp', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async sendBitstampMainToSub ({ date, currency }) {
      date = new Date(date)
      date = new Date(date)
      return await request('/sales/send-bitstamp-main-to-sub', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async saleSummary (start = new Date()) {
      return await request('/sales/summary', {
        sale_date_id: start.toISOString().split('T')[0]
      }).then(r => r.data.result)
    },
    async salesResults ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/sales-results', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async wakeUpDispatch ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/wake-up-dispatch', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async dispatch ({ date, currency }) {
      date = new Date(date)
      return await request('/sales/dispatch', { sale_date_id: date.toISOString().split('T')[0], token: currency}).then(r => r.data.result)
    },
    async checkBeforeSendToDaily () {
      return await request('/sales-process-check/check-before-send-to-daily').then(r => r.data.result)
    },
    async checkBeforeSendToExchanges () {
      return await request('/sales-process-check/check-before-send-to-exchanges').then(r => r.data.result)
    },
    async updateWakeUpSpreadsheet () {
      return await request('/sales/spreadsheet-wake-up').then(r => r.data.result)
    },
    async updateResultSpreadsheet () {
      return await request('/sales/spreadsheet-results').then(r => r.data.result)
    },
    async validateWithdrawAndDepositResult (saleDateId, exchanges) {
      return await request('/sales/write-validate-result', { 'sale_date_id': saleDateId, exchanges }).then(r => r.data.result)
    }
  },
  notifier: {
    async notifySaleBalance () {
      return await request('/notifier/sales-bot-balance').then(r => r.data.result)
    },
    async checkEmptyBots () {
      return await request('/notifier/sales-empty-alert').then(r => r.data.result)
    },
    async checkClientBuffers () {
      return await request('/notifier/client-buffer-balance').then(r => r.data.result)
    },
    async checkClientConfigSummary () {
      return await request('/notifier/sales-client-config-summary').then(r => r.data.result)
    },
    async checkSendToDaily () {
      return await request('/notifier/check-send-daily-to-exchange').then(r => r.data.result)
    },
    async checkEstimateBTCResult () {
      return await request('/notifier/sale-estimate-btc-result').then(r => r.data.result)
    },
    async checkWithdrawRecords () {
      return await request('/notifier/validate-withdraw-to-bitstamp').then(r => r.data.result)
    },
    async checkExecutions () {
      return await request('/notifier/monitor-current-sale-target').then(r => r.data.result)
    },
    async notifyBotVirtualBalance () {
      return await request('/notifier/bot-virtual-balance').then(r => r.data.result)
    },
    async notifyAnyBotBalance () {
      return await request('/notifier/monitor-any-bot-balance').then(r => r.data.result)
    },
    async notifyERC20TokenAlert () {
      return await request('/notifier/erc20-token-alert').then(r => r.data.result)
    },
    async notifierSummaryToTelegram () {
      return await request('/notifier/notifier-summary-to-telegram').then(r => r.data.result)
    },
    async notifierSummaryToTelegram2 () {
      return await request('/notifier/notifier-summary-to-telegram-v2').then(r => r.data.result)
    }
  },
  botControl: {
    async startTodaySaleBots ({currency}) {
      return await request('/bot-control/start-sales-bots', {token: currency}).then(r => r.data.result)
    },
    async stopTodaySaleBots ({currency}) {
      return await request('/bot-control/stop-sales-bots', {token: currency}).then(r => r.data.result)
    },
    async checkBotStatus ({currency}) {
      return await request('/bot-control/check-sale-bot-is-active', {token: currency}).then(r => r.data.result)
    },
    async cancelBotOrders (accountIds, side = 'both') {
      return await request('/bot-control/cancel-orders', {accountIds: accountIds, side: side}).then(r => r.data.result)
    }
  },
  fund: {
    async accountCurrencies () {
      return await request('/funds/account-currencies').then(r => r.data.result)
    },
    async balancesBitstampSubAccount ({token}) {
      return await request('/funds/balances-bitstamp-sub-account',{token}).then(r => r.data.result)
    },
    async internalBitstampFund ({from, to, currency, amount}) {
      return await request('/funds/internal-bitstamp-fund',
      { from_account_id: from, to_account_id: to, currency, amount }).then(r => r.data.result)
    },
    async transfer (from, to, currency, amount) {
      // return {success: true}
      throw new Error('Serivce Unavailable')
    }
  },
  cloudwatch: {
    async search ({ logGroupName, requestId, startTime, endTime }) {
      return await request('/cloudwatch/search', { logGroupName, requestId, startTime, endTime }).then(r => (r.data.result || [])[0] || 'Log not found')
    }
  },
  googleCloudStorage: {
    async list ({ gcsKey, bucket, directory }) {
      return await request('/api/google-cloud-storage/list', { gcsKey, bucket, directory }).then(r => r.data)
    },
    async delete ({ gcsKey, bucket, filepath }) {
      return await request('/api/google-cloud-storage/delete', { gcsKey, bucket, filepath }).then(r => r.data)
    },
    async upload ({ gcsKey, bucket, destination, base64 }) {
      return await request('/api/google-cloud-storage/upload', { gcsKey, bucket, destination, base64 }, true).then(r => r.data)
    },
    async download ({ gcsKey, bucket, filepath }) {
      return await request('/api/google-cloud-storage/download', { gcsKey, bucket, filepath })
        .then(res => {
          const { name, base64 } = res.data
          const downloadLink = document.createElement("a")
          downloadLink.href = base64
          downloadLink.download = name
          downloadLink.click()
        })
    }
  },
  accountConfig: {
    async insert ({ pair, basic }) {
      const map = (pair, basic) => ({
        account_id: pair.name,
        report_account_id: pair.reportName,
        client_id: basic.client,
        type: pair.type,
        exchange: basic.exchange,
        sub_account: pair.subAccount,
        api_keys: pair.api,
        isAddReport: pair.isAddReport,
        exchange_username: pair.exchange_username
      })

      const account = map(pair, basic)
      return request('/api/account-config/insert', { account }).then(r => r.data.result)
    },
  },
  spreadsheetOperation: {
    async refreshBalances (params) {
      return request('/api/spreadsheet-operation/refresh-balance', params).then(r => r.data.result)
    },
    async refreshOrderBook (params) {
      return request('/api/spreadsheet-operation/refresh-orderbook', params).then(r => r.data.result)
    },
    async refreshOpenOrders (params) {
      return request('/api/spreadsheet-operation/refresh-openorder', params).then(r => r.data.result)
    },
    async botReportRequest (params) {
      return request('/api/spreadsheet-operation/bot-report', params, true, { timeout: 120000 }).then(r => r)
    }
  },
  buybackOperation: {
    async buyback (params) {
      return request('/api/buyback-operation/buyback', params).then(r => r.data.result)
    }
  }
}

export default service
